import { notifySlackError } from 'utils/slack/slackNotifier';

const DEFAULT_API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface FetchOptions extends RequestInit {
	requireAuth?: boolean;
	baseUrl?: string;
}

export class ApiError extends Error {
	constructor(message: string, public status: number, public data?: any) {
		super(message);
		this.name = 'ApiError';
	}
}

async function apiFetch<T>(
	endpoint: string,
	options: FetchOptions = {},
): Promise<T> {
	const { requireAuth, baseUrl, ...fetchOptions } = options;
	const resolvedBaseUrl = baseUrl || DEFAULT_API_BASE_URL;

	const headers: HeadersInit = {
		'Content-Type': 'application/json',
		'x-api-key': API_KEY,
		...fetchOptions.headers,
	};

	try {
		const response = await fetch(`${resolvedBaseUrl}${endpoint}`, {
			...fetchOptions,
			headers,
		});

		const data = await response.json();

		if (!response.ok) {
			const apiError = new ApiError(
				data.error || `API Error: ${response.status}`,
				response.status,
				data,
			);

			notifySlackError({
				title: `API Error ${resolvedBaseUrl}${endpoint}`,
				details: '',
			});

			throw apiError;
		}

		return data as T;
	} catch (error) {
		if (error instanceof ApiError) {
			throw error;
		}

		const networkError = new ApiError(
			error instanceof Error ? error.message : 'Network error',
			0,
		);

		notifySlackError({
			title: 'Network Failure' + networkError.message,
			details: '',
		});

		throw networkError;
	}
}

export interface CreateUserRequest {
	walletAddress: string;
	email: string;
}

export interface CreateUserResponse {
	id: string;
	error?: string;
}

export interface LastDeposit {
	amountBase: string;
	posId: number;
	at: string;
	txHash: string;
}

export interface User {
	id: string;
	userId: string;
	walletAddress: string;
	email: string;
	status: string;
	firstDepositAt: string | null;
	lastDeposit: LastDeposit | null;
	createdAt: { _seconds: number; _nanoseconds: number };
	updatedAt: { _seconds: number; _nanoseconds: number };
}

export async function createUser(
	request: CreateUserRequest,
	baseUrl?: string,
): Promise<CreateUserResponse> {
	return apiFetch<CreateUserResponse>('/api/users', {
		method: 'POST',
		body: JSON.stringify(request),
		baseUrl,
	});
}

export async function getUser(userId: string, baseUrl?: string): Promise<User> {
	return apiFetch<User>(`/api/users/me?userId=${userId}`, { baseUrl });
}

export interface PortfolioResponse {
	ok: boolean;
	userId: string;
	quoteSpot: boolean;
	includeClosed: boolean;
	scope: string;
	inv: string;
	totals: {
		totalBase: string;
		total: string;
		investedBase: string;
		invested: string;
		roi: number;
		apySinceDeposit: number;
		apySinceAllocation: number;
	};
	positions: Position[];
}

export interface Position {
	posId: number;
	poolId: string;
	status: string;
	components: {
		unrealizedBase: string;
		creditedBase: string;
	};
	totalBase: string;
	total: string;
	depositBase: string;
	deposit: string;
	daysSinceDeposit: number;
	daysSinceAllocation: number;
	roi: number;
	unrealizedBreakdown: {
		valueUsdcBase: string;
		usdtToUsdcBase: string;
		aptToUsdcBase: string;
		totalUnrealizedBase: string;
	};
}

export async function getUserApyValue(
	userId: string,
	quote = true,
	baseUrl?: string,
): Promise<PortfolioResponse> {
	return apiFetch<PortfolioResponse>(
		`/api/users/${userId}/value?quote=${quote}&scope=allocated`,
		{ baseUrl },
	);
}

export async function getUserLastPositionId(
	userId: string,
	baseUrl?: string,
): Promise<number | null> {
	try {
		const user = await getUser(userId, baseUrl);
		return user.lastDeposit?.posId ?? null;
	} catch (error) {
		notifySlackError({
			title: 'Failed getUserLastPositionId' + `User ID: ${userId}`,
			details: ``,
			address: userId,
		});
		return null;
	}
}

export async function createOrGetUser(
	walletAddress: string,
	email?: string,
	baseUrl?: string,
): Promise<{ id: string; isNewUser: boolean }> {
	try {
		// console.log('baseUrl', baseUrl);
		const response = await createUser(
			{
				walletAddress,
				email: email || 'not-provided@example.com',
			},
			baseUrl,
		);

		if (response.error && response.error.includes('already exists')) {
			return { id: response.id, isNewUser: false };
		}

		return { id: response.id, isNewUser: true };
	} catch (error) {
		console.log('called');
		if (error instanceof ApiError && error.status === 409) {
			return {
				id: error.data?.id || '',
				isNewUser: false,
			};
		}
		throw error;
	}
}

export interface RecordDepositRequest {
	txHash: string;
	userId: string;
	posId: number;
	amountBase: string;
}

export interface RecordDepositResponse {
	ok: boolean;
}

export async function recordDeposit(
	request: Pick<RecordDepositRequest, 'txHash'>,
	baseUrl?: string,
): Promise<RecordDepositResponse> {
	// console.log('deposit start');
	return apiFetch<RecordDepositResponse>('/api/intake/deposit', {
		method: 'POST',
		body: JSON.stringify(request),
		baseUrl,
	});
}

/*export async function recordDeposit(
	request: RecordDepositRequest,
): Promise<RecordDepositResponse> {
	return apiFetch<RecordDepositResponse>('/api/users/record-deposit', {
		method: 'POST',
		body: JSON.stringify(request),
	});
}*/

export interface RecordWithdrawRequest {
	userId: string;
	posId: number;
	creditedBase: string;
	txHash: string;
}

export interface RecordWithdrawResponse {
	ok: boolean;
}

export interface FinalizeRequest {
	userId: string;
	posId: number;
	sweepFeesFirst?: boolean;
}

export interface FinalizeResponse {
	ok: boolean;
}

/*export async function recordWithdraw(
	request: RecordWithdrawRequest,
): Promise<RecordWithdrawResponse> {
	return apiFetch<RecordWithdrawResponse>('/api/users/record-withdraw', {
		method: 'POST',
		body: JSON.stringify(request),
	});
}*/

export async function finalizePosition(
	request: FinalizeRequest,
	baseUrl?: string,
): Promise<FinalizeResponse> {
	return apiFetch<FinalizeResponse>('/api/agent/finalize', {
		method: 'POST',
		body: JSON.stringify({
			...request,
			sweepFeesFirst:
				request.sweepFeesFirst !== undefined
					? request.sweepFeesFirst
					: true,
		}),
		baseUrl,
	});
}

export interface NextPosIdResponse {
	nextPosId: number;
}

export async function getNextPosId(
	baseUrl?: string,
): Promise<NextPosIdResponse> {
	return apiFetch<NextPosIdResponse>('/api/chain/next-pos-id', {
		baseUrl,
	});
}

export interface PositionResult {
	id: number;
	result: [string, string | number, string | number, boolean] | null;
}

function normalizePositionResult(data: any): PositionResult {
	if (!data) {
		return { id: 0, result: null };
	}

	const { id, result } = data;

	if (!result) {
		return { id, result: null };
	}

	if (Array.isArray(result)) {
		return {
			id,
			result: result as [
				string,
				string | number,
				string | number,
				boolean,
			],
		};
	}

	if (typeof result === 'object' && result.owner !== undefined) {
		return {
			id,
			result: [
				data.result.owner,
				data.result.vaultBalance || 0,
				data.result.credited || 0,
				data.result.finalized || false,
			],
		};
	}

	// console.warn('Unknown position format:', data);
	return { id: data.id, result: null };
}

export async function getPosition(
	posId: number,
	baseUrl?: string,
): Promise<PositionResult> {
	try {
		const raw = await apiFetch<any>(`/api/chain/position/${posId}`, {
			baseUrl,
		}); //${posId}
		return normalizePositionResult(raw);
	} catch (error) {
		notifySlackError({
			title: 'getPosition API Failed' + `Fallback used — PosID: ${posId}`,
			details: '',
		});

		const { getPosition: contractCall } = await import(
			'../core/vaultTransactions'
		);
		const result = await contractCall(posId);

		if (!result) return { id: posId, result: null };

		return {
			id: posId,
			result: [
				result.user,
				result.vault_balance,
				result.credited,
				result.finalized,
			],
		};
	}
}
